import { ThemeFont, ThemeMode } from '@prisma/client'
import type { MetaFunction } from '@remix-run/node'
import { Link, Outlet, useLoaderData } from '@remix-run/react'
import {
  json,
  type LinksFunction,
  type LoaderFunctionArgs,
} from '@remix-run/server-runtime'
import { use, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CartContextProvider, CartDrawer } from '~/components/cart'
import { GeneralErrorBoundary } from '~/components/error-boundary'
import { ErrorPage } from '~/components/ErrorPage'
import { Layout } from '~/components/layout'
import { LogoHorizontal } from '~/components/Logo'
import { MediaManager } from '~/components/MediaManager'
import { Seo } from '~/components/Seo'
import { ShopContext } from '~/contexts'
import type { ShopContextData } from '~/contexts/ShopContext'
import { calculatePublicCartPriceDetail } from '~/helpers/cart'
import { makeTitle } from '~/helpers/pages'
import { makeMainPath, makeShopPath } from '~/helpers/routes'
import { useCurrentPage } from '~/hooks/useCurrentPage'
import { getCurrentOrRecoverCart } from '~/models/cart.server'
import { getShopMetadata } from '~/models/shop.server'
import {
  Navbar,
  NavbarTrigger,
  useNavbarContext,
} from '~/routes/shop.$shopSlug/components/navbar'
import { ShopLayoutProviders } from '~/routes/shop.$shopSlug/components/providers'
import {
  ThemeCustomizer,
  ThemeCustomizerTrigger,
  useThemeCustomizerContext,
} from '~/routes/shop.$shopSlug/components/theme-customizer'
import { VisibilityConfirmationModal } from '~/routes/shop.$shopSlug/components/VisibilityConfirmationModal'
import { impersonatedSellerIdKey } from '~/services/auth-admin.server'
import { getBuyer } from '~/services/auth-buyer.server'
import { requireShopAndItsOptionalSeller } from '~/services/auth-shop.server'
import { getGeoCountry } from '~/services/geoip.server'
import { getApexSession } from '~/services/session-apex.server'
import {
  buildCommitShopSessionHeaders,
  getShopSession,
} from '~/services/session-shop.server'
import { Theme } from '~/themes'
import { createShopStyleString } from '~/themes/css'
import { Button } from '~/ui'
import { SEO_IMAGE_OPTIONS } from '~/utils/constants'
import { generateShopLogoFormats, getImageKitUrl } from '~/utils/image'
import { generateSeoImage } from '~/utils/seo-helpers'

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: `/fonts.css?v=2023-11-28` },
]

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop, seller } = await requireShopAndItsOptionalSeller(
    loaderArgs.request
  )

  const [buyer, apexSession, shopSession] = await Promise.all([
    getBuyer(shop, loaderArgs.request),
    getApexSession(loaderArgs.request),
    getShopSession(loaderArgs.request),
  ])

  const impersonatedSellerId = apexSession.get(impersonatedSellerIdKey)

  const sessionCartId = shopSession.get('cartId')
  let buyerCountry = shopSession.get('buyerCountry')

  const [cart, analyticsEnabled, geoCountry] = await Promise.all([
    getCurrentOrRecoverCart(shop.id, sessionCartId, buyer?.id),
    Promise.resolve(apexSession.get('analyticsEnabled')),
    buyerCountry ? undefined : getGeoCountry(loaderArgs.request),
  ])

  let commitSession = false
  if (geoCountry) {
    buyerCountry = geoCountry
    shopSession.set('buyerCountry', geoCountry)
    commitSession = true
  }

  let publicCartPriceDetail = cart
    ? calculatePublicCartPriceDetail({ shop, cart, buyerCountry })
    : undefined

  const shopLogos = await generateShopLogoFormats(shop.themeColor)
  const shopLogo = shopLogos.png || makeMainPath(`/favicon.png`)
  const shopLogoIco = shopLogos.ico || makeMainPath(`/favicon.ico`)

  const shopSeoImage = shop.avatarSrc
    ? getImageKitUrl(shop.avatarSrc).startsWith('http')
      ? generateSeoImage(shop.avatarSrc, SEO_IMAGE_OPTIONS)
      : `${process.env.APP_URL}${generateSeoImage(shop.avatarSrc, SEO_IMAGE_OPTIONS)}`
    : `/gradient.png`

  const canonicalUrl = seller
    ? makeMainPath(`/shop/${shop.slug}`)
    : makeShopPath(shop, '')

  const metadata = await getShopMetadata(shop.id)

  return json(
    {
      shop,
      metadata,
      seller,
      buyer,
      cart,
      buyerCountry,
      publicCartPriceDetail,
      iSellerId: impersonatedSellerId,
      title: makeTitle(shop.name),
      analyticsEnabled,
      seoInfo: {
        canonicalUrl,
        shopLogo,
        shopLogoIco,
        shopSeoImage,
      },
    },
    commitSession
      ? { headers: await buildCommitShopSessionHeaders(shopSession) }
      : undefined
  )
}

export const meta: MetaFunction<typeof loader> = ({ error, data }) => {
  if (error && typeof error === 'object' && 'status' in error) {
    const err = error as { status: number; statusText?: string }
    return [
      {
        title: `Error ${err.status} – ${err.statusText || 'Link confirmation'}`,
      },
    ]
  }

  if (data && typeof data === 'object' && 'shop' in data) {
    const { shop, seoInfo } = data

    const description = shop.description || `${shop.name} - Online Store`

    return [
      { title: makeTitle(shop.name) },
      {
        name: 'description',
        content: description,
      },

      // Favicon - Prioritize shop branding, with ICO fallback for older browsers
      {
        tagName: 'link',
        rel: 'icon',
        type: 'image/png',
        href: seoInfo.shopLogo,
      },
      {
        tagName: 'link',
        rel: 'shortcut icon',
        type: 'image/png',
        href: seoInfo.shopLogo,
      },
      {
        tagName: 'link',
        rel: 'apple-touch-icon',
        href: seoInfo.shopLogo,
      },
      // Fallback ICO for older browsers that don't support PNG
      {
        tagName: 'link',
        rel: 'icon',
        type: 'image/x-icon',
        href: seoInfo.shopLogoIco,
      },

      // Open Graph / Facebook
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: shop.name },
      { property: 'og:description', content: description },
      ...(seoInfo.canonicalUrl
        ? [
            {
              property: 'og:url',
              content: seoInfo.canonicalUrl,
            },
          ]
        : []),
      ...(shop.avatarSrc
        ? [
            {
              property: 'og:image',
              content: seoInfo.shopSeoImage,
            },
          ]
        : []),
      { property: 'og:site_name', content: 'Protocol' },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: shop.name },
      { name: 'twitter:description', content: description },
      ...(shop.avatarSrc
        ? [
            {
              name: 'twitter:image',
              content: seoInfo.shopSeoImage,
            },
          ]
        : []),

      ...(seoInfo.canonicalUrl
        ? [
            {
              tagName: 'link',
              rel: 'canonical',
              href: seoInfo.canonicalUrl,
            },
          ]
        : []),
    ]
  }

  return [{ title: 'Unexpected Error – Link confirmation' }]
}

export default function ShopLayoutRoot() {
  const { shop } = useLoaderData<typeof loader>()

  const [contextShop, setContextShop] = useState<ShopContextData>({
    ...shop,
    themeMode: shop.themeMode || ThemeMode.light,
    themeFont: shop.themeFont || ThemeFont.sans,
  })

  return (
    <ShopContext value={[contextShop, setContextShop]}>
      <ShopLayoutProviders>
        <style
          dangerouslySetInnerHTML={{
            __html: createShopStyleString(contextShop),
          }}
        />
        <ShopLayoutPage />
      </ShopLayoutProviders>
    </ShopContext>
  )
}

const ShopLayoutPage = () => {
  const { seller, cart, buyerCountry, publicCartPriceDetail } =
    useLoaderData<typeof loader>()
  const [contextShop] = use(ShopContext)

  const page = useCurrentPage()

  const { isOpen: isNavbarOpen } = useNavbarContext()
  const { isOpen: isThemeCustomizerOpen } = useThemeCustomizerContext()

  const theme = ['home', 'product', 'collection', 'legal'].includes(page)
    ? 'shop'
    : 'protocol-dark'

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: createShopStyleString(contextShop),
        }}
      />

      {/* Remove background gradient for now as it as it clashes a lot with some store colors (themes) and has some impact on performance */}
      {/*<BackgroundGradient />*/}

      <Seo />

      {seller && (
        <Layout
          data-theme="protocol-dark"
          className="bg-background"
          isLeftOpen={isNavbarOpen}
          isRightOpen={isThemeCustomizerOpen}
        >
          <Layout.TopBar>
            <Link to={makeMainPath('/')}>
              <LogoHorizontal className="absolute bottom-3 left-1/2 w-20 -translate-x-1/2" />
            </Link>
          </Layout.TopBar>

          <Layout.Aside side="left">
            <Layout.AsideTrigger className="cursor-pointer">
              <NavbarTrigger variant={isNavbarOpen ? 'close' : 'open'} />
            </Layout.AsideTrigger>
            <Navbar className="max-sm:mr-2" />
          </Layout.Aside>

          {theme === 'shop' && (
            <Layout.Aside side="right">
              <Layout.AsideTrigger>
                <ThemeCustomizerTrigger />
              </Layout.AsideTrigger>
              <Layout.AsideConditionalContent className="h-full max-sm:ml-2">
                <ThemeCustomizer />
              </Layout.AsideConditionalContent>
            </Layout.Aside>
          )}
          <Theme theme={theme}>
            <Layout.Main
              id="shop-container"
              className="@container"
              style={{ containerType: 'size' }}
            >
              <Outlet />
              <MediaManager />
              <VisibilityConfirmationModal />
            </Layout.Main>
          </Theme>
        </Layout>
      )}

      {!seller && (
        <Theme theme="shop">
          <main
            id="shop-container"
            className="@container h-full"
            style={{ containerType: 'size' }}
          >
            <CartContextProvider>
              <Outlet />
              <CartDrawer
                cart={cart}
                buyerCountry={buyerCountry}
                priceDetail={publicCartPriceDetail || null}
              />
            </CartContextProvider>
          </main>
        </Theme>
      )}
    </>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation()

  const errorStatusHandlers = {
    404: ({
      error,
      params,
    }: {
      error: { data: string; status: number }
      params: Record<string, string | undefined>
    }) => {
      const isShopMissing = error.data === 'Shop Not Found'

      return (
        <ErrorPage
          title={
            isShopMissing
              ? t('errors.shop_not_found')
              : t('page.not_found.title')
          }
          description={
            isShopMissing
              ? t('errors.shop_not_found_description')
              : t('page.not_found.description')
          }
        >
          {isShopMissing || !params.shopSlug ? (
            <Button size="lg" asChild>
              <a href="https://protocol.store/">{t('action.go_to_homepage')}</a>
            </Button>
          ) : (
            <Button size="lg" asChild>
              <a href={makeShopPath(params.shopSlug, '')}>
                {t('action.continue_shopping')}
              </a>
            </Button>
          )}
        </ErrorPage>
      )
    },
  }

  return (
    <div className="@container">
      <GeneralErrorBoundary
        unexpectedErrorHandler={() => (
          <ErrorPage
            title={t('errors.there_was_an_error')}
            description={t('errors.page_temporarily_unavailable')}
          >
            <Button onClick={() => window.location.reload()}>
              {t('action.try_again')}
            </Button>
            <Button variant="outline" onClick={() => window.history.back()}>
              {t('action.go_back')}
            </Button>
          </ErrorPage>
        )}
        statusHandlers={errorStatusHandlers}
      />
    </div>
  )
}
